/* Falk MFG customer-facing quote UI. Prices remain preliminary until reviewed. */
(() => {
  const money = value => new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(value);
  const number = value => Number(value);
  const set = (root, selector, value) => { root.querySelector(selector).textContent = value; };
  const fileFormat = name => /\.(step|stp)$/i.test(name) ? 'step' : /\.3mf$/i.test(name) ? '3mf' : /\.stl$/i.test(name) ? 'stl' : /\.obj$/i.test(name) ? 'obj' : null;

  const price = (geometry, material, quantity, config) => {
    const effectiveCm3 = Math.min(geometry.volumeCm3, geometry.volumeCm3 * config.infill / 100 + geometry.areaCm2 * config.shell / 10);
    const massG = effectiveCm3 * material.density;
    const hours = (massG / config.throughput + geometry.dimensions[2] / config.layerHeight * config.layerSeconds / 3600) * material.timeFactor;
    const materialLine = massG * material.price / 1000 * quantity;
    const machineLine = hours * config.machineRate * quantity;
    const finishingLine = config.post * quantity;
    const discountRate = quantity >= 100 ? .15 : quantity >= 50 ? .10 : quantity >= 10 ? .05 : 0;
    const discountLine = (materialLine + machineLine + finishingLine) * discountRate;
    const setupLine = config.setup + config.packaging + material.prep;
    const riskLine = (materialLine + machineLine + finishingLine - discountLine + config.setup + material.prep) * config.risk / 100;
    const subtotal = materialLine + machineLine + finishingLine + setupLine + riskLine - discountLine;
    return { massG, hours, materialLine, machineLine, finishingLine, setupLine, discountLine, riskLine, minimumAdjustment: Math.max(0, config.minimum - subtotal), total: Math.max(config.minimum, subtotal) };
  };

  const readModel = async (root, format, buffer) => {
    const workerUrl = root.dataset.workerUrl;
    let worker;
    let blobUrl;
    try {
      if (new URL(workerUrl, location.href).origin === location.origin) {
        worker = new Worker(workerUrl);
      } else {
        const response = await fetch(workerUrl);
        if (!response.ok) throw new Error('read');
        blobUrl = URL.createObjectURL(new Blob([await response.text()], { type: 'text/javascript' }));
        worker = new Worker(blobUrl);
      }
      return await new Promise((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('read')), 120000);
        worker.onmessage = event => {
          clearTimeout(timer);
          event.data.error ? reject(new Error(event.data.error)) : resolve(event.data.geometry);
        };
        worker.onerror = () => { clearTimeout(timer); reject(new Error('read')); };
        worker.postMessage({ id: 1, format, buffer, scriptUrl: root.dataset.stepScriptUrl, wasmUrl: root.dataset.stepWasmUrl, depsUrl: root.dataset.threeMfDepsUrl }, [buffer]);
      });
    } finally {
      worker?.terminate();
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    }
  };

  const init = root => {
    if (root.dataset.initialized) return;
    root.dataset.initialized = '1';
    const button = root.querySelector('[data-calculate]');
    const error = root.querySelector('[data-error]');
    const result = root.querySelector('[data-result]');
    const contactBody = root.querySelector('[data-contact-body]');
    let generatedContact = '';
    const writeContact = text => { contactBody.value = text; generatedContact = text; };
    const materialSelect = root.querySelector('[data-material]');
    const colorSelect = root.querySelector('[data-color]');
    const resetEstimate = () => {
      result.hidden = true;
      error.hidden = true;
      if (contactBody.value === generatedContact) contactBody.value = '';
    };
    const updateMaterial = () => {
      for (const color of colorSelect.options) {
        color.disabled = !color.dataset.materials.split(',').includes(materialSelect.value);
        color.hidden = color.disabled;
      }
      if (colorSelect.selectedOptions[0].disabled) colorSelect.value = 'black';
      set(root, '[data-material-help]', materialSelect.selectedOptions[0].dataset.description);
      resetEstimate();
    };
    materialSelect.addEventListener('change', updateMaterial);
    for (const field of root.querySelectorAll('[data-color],[data-quantity],[data-model-file]')) field.addEventListener('change', resetEstimate);
    updateMaterial();
    button.addEventListener('click', async () => {
      error.hidden = true;
      result.hidden = true;
      const file = root.querySelector('[data-model-file]').files[0];
      const format = file && fileFormat(file.name);
      const quantity = number(root.querySelector('[data-quantity]').value);
      if (!format) { error.textContent = root.dataset.errorFormat; error.hidden = false; return; }
      if (!Number.isInteger(quantity) || quantity < 1 || quantity > 1000) { error.textContent = root.dataset.errorQuantity; error.hidden = false; return; }
      const option = root.querySelector('[data-material]').selectedOptions[0];
      const color = colorSelect.selectedOptions[0].textContent.trim();
      const requestText = `Model: ${file.name}\nMaterial: ${option.textContent.trim()}\nColor: ${color}\nQuantity: ${quantity}\n\nPlease describe the part's use, timeline, and any fit or finish requirements. The CAD file stays on my device; please request it by email.`;
      writeContact(requestText);
      if (file.size > 80 * 1024 * 1024 || file.size === 0) { error.textContent = root.dataset.errorSize; error.hidden = false; return; }
      button.disabled = true;
      button.textContent = button.dataset.loadingLabel;
      try {
        const geometry = await readModel(root, format, await file.arrayBuffer());
        const config = Object.fromEntries(['minimum','setup','machineRate','throughput','infill','shell','layerHeight','layerSeconds','post','packaging','risk','maxDimension'].map(key => [key, number(root.dataset[key])]));
        if (Object.values(config).some(value => !Number.isFinite(value) || value < 0) || config.throughput === 0 || config.layerHeight === 0) throw new Error('read');
        const dimensions = geometry.dimensions.map(value => value.toFixed(1)).join(' × ');
        const review = !Number.isFinite(geometry.volumeCm3) || geometry.volumeCm3 <= 0;
        const notes = [];
        if (review) notes.push(root.dataset.noteOpen);
        if (geometry.assembly) notes.push(root.dataset.noteBodies);
        if (geometry.dimensions.some(value => value > config.maxDimension)) notes.push(root.dataset.noteSize);
        set(root, '[data-model-details]', `${dimensions} mm${geometry.volumeCm3 === null ? '' : ` · ${geometry.volumeCm3.toFixed(2)} cm³`}`);
        root.querySelector('[data-review-note]').hidden = !notes.length;
        set(root, '[data-review-note]', notes.join(' '));
        root.querySelector('[data-unit-price]').hidden = review;
        root.querySelector('[data-production-window]').hidden = review;
        root.querySelector('[data-empty-note]').hidden = true;
        if (review) {
          set(root, '[data-total]', 'Manual review');
          writeContact(`${requestText}\nApproximate dimensions: ${dimensions} mm\nAutomatic estimate: manual review needed`);
        } else {
          const material = { density: number(option.dataset.density), price: number(option.dataset.price), timeFactor: number(option.dataset.timeFactor), prep: number(option.dataset.prep) };
          if (!Object.values(material).every(Number.isFinite) || material.density <= 0 || material.price <= 0 || material.timeFactor <= 0 || material.prep < 0) throw new Error('read');
          const quote = price(geometry, material, quantity, config);
          if (!Number.isFinite(quote.total)) throw new Error('read');
          set(root, '[data-total]', money(quote.total));
          const unitPrice = `${money(quote.total / quantity)} ${geometry.assembly ? root.dataset.unitSet : root.dataset.unitPart}`;
          set(root, '[data-unit-price]', unitPrice);
          set(root, '[data-model-details]', `${dimensions} mm`);
          const scheduleConfig=Object.fromEntries(['standardPrinters','largePrinters','queueDays','sourceDays','handlingDays','weekdayHours','flexHours'].map(key=>[key,number(root.dataset[key])]));
          scheduleConfig.buffer=number(root.dataset.scheduleBuffer);
          const schedule=globalThis.FalkBetaSchedule?.estimate({hours:quote.hours,quantity,dimensions:geometry.dimensions,material:option.value,color:colorSelect.value,config:scheduleConfig});
          const formatDate=key=>new Intl.DateTimeFormat(undefined,{month:'short',day:'numeric',year:'numeric',timeZone:'UTC'}).format(new Date(`${key}T12:00:00Z`));
          const timing=schedule?`${root.dataset.readyLabel}: ${formatDate(schedule.from)} – ${formatDate(schedule.to)}`:root.dataset.readyUnavailable;
          set(root,'[data-production-window]',timing);
          writeContact(`${requestText}\nPreliminary order estimate: ${money(quote.total)}\nUnit estimate: ${unitPrice}\n${timing}\nApproximate dimensions: ${dimensions} mm\n${notes.join(' ')}`);
        }
        result.hidden = false;
      } catch (cause) {
        error.textContent = cause?.message === 'size' ? root.dataset.errorSize : cause?.message === 'mesh' ? root.dataset.errorMesh : root.dataset.errorRead;
        error.hidden = false;
      } finally {
        button.disabled = false;
        button.textContent = button.dataset.idleLabel;
      }
    });
  };
  document.querySelectorAll('[data-falk-print]').forEach(init);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-falk-print]').forEach(init));
})();
